import React from 'react';

interface RootErrorBoundaryState {
  error: Error | null;
}

/**
 * Last-resort boundary at the application root (task 349). Any render-time
 * crash in the provider stack or below produces a minimal visible failure
 * screen naming the error, instead of React unmounting the tree to a blank
 * page (the prd blank-page incident: FlagProvider throwing on an undefined
 * Unleash url left #root empty with the cause visible only in the console).
 *
 * Deliberately dependency-free (no theme, router, or context) so it can
 * render no matter which provider crashed.
 */
export class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[root-error-boundary] Render crash:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            fontFamily: 'system-ui, sans-serif',
            maxWidth: '40rem',
            margin: '4rem auto',
            padding: '0 1.5rem',
            color: '#e6edf3',
          }}
        >
          <h1 style={{ fontSize: '1.25rem' }}>Innovate failed to start</h1>
          <p>
            Something went wrong while starting the application. Refresh to try
            again; if it persists, contact support with the message below.
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: 'rgba(128,128,128,0.15)',
              padding: '0.75rem',
              borderRadius: '6px',
            }}
          >
            {this.state.error.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
